import React from "react";
import { useComposerContext } from "../context/ComposerContext";
import {
  Bold, Italic, Underline, Heading2, Heading3, Pilcrow,
  List, ListOrdered, Link, AlignLeft, AlignCenter, AlignRight, RemoveFormatting,
  Undo2, Redo2, Share2,
} from "lucide-react";

function TBtn({ icon: Icon, title, onClick, label }) {
  return (
    <button
      type="button"
      title={title}
      onMouseDown={(event) => event.preventDefault()}
      onClick={onClick}
      className="p-1.5 min-h-[36px] min-w-[36px] shrink-0 flex items-center justify-center rounded-md hover:bg-bookish-purple/10 text-bookish-purple/70 hover:text-bookish-purple transition-colors"
    >
      {Icon ? <Icon size={16} /> : <span className="text-xs font-semibold px-0.5">{label}</span>}
    </button>
  );
}

function Divider() {
  return <div className="w-px h-5 bg-bookish-purple/12 mx-0.5" />;
}

export default function EditorCanvas({ exec, insertLink, clearFormatting, normalizeEditor, onKeyDown, onUndo, onRedo, onAddTextBlock, onBrowseTemplates, onClearCanvas, onInsertInlineSocialSnippet }) {
  const {
    loading,
    uploading,
    previewMode,
    contentWidth,
    outerBg,
    cardBg,
    textColor,
    fontFamily,
    fontSize,
    editorRef,
  } = useComposerContext();

  const isMobile = previewMode === "mobile";
  const editorMaxWidth = isMobile ? "375px" : contentWidth;
  const editorPadding = isMobile ? "10px" : "12px";
  const workspaceMaxWidth = isMobile ? "420px" : "960px";

  return (
    <div className="flex-1 min-w-0 bg-gradient-to-b from-white to-bookish-cream/20">
      <div className="flex h-full min-w-0 flex-col md:max-h-[calc(100vh-var(--composer-header-offset))]">


        <div className="sticky top-[var(--composer-header-offset)] z-[60] w-full border-b border-bookish-purple/8 bg-white px-4 py-3">
          <div className="mx-auto flex w-full max-w-[920px] flex-wrap items-center gap-0.5 rounded-2xl border border-bookish-purple/12 bg-bookish-cream/40 px-1.5 py-1 shadow-sm">
            <TBtn icon={Bold} title="Bold" onClick={() => exec("bold")} />
            <TBtn icon={Italic} title="Italic" onClick={() => exec("italic")} />
            <TBtn icon={Underline} title="Underline" onClick={() => exec("underline")} />

            <Divider />

            <TBtn icon={Heading2} title="Heading 2" onClick={() => exec("formatBlock", "H2")} />
            <TBtn icon={Heading3} title="Heading 3" onClick={() => exec("formatBlock", "H3")} />
            <TBtn icon={Pilcrow} title="Paragraph" onClick={() => exec("formatBlock", "P")} />

            <Divider />

            <TBtn icon={List} title="Bulleted list" onClick={() => exec("insertUnorderedList")} />
            <TBtn icon={ListOrdered} title="Numbered list" onClick={() => exec("insertOrderedList")} />
            <TBtn icon={Link} title="Insert link" onClick={insertLink} />

            <Divider />

            <TBtn icon={AlignLeft} title="Align left" onClick={() => exec("justifyLeft")} />
            <TBtn icon={AlignCenter} title="Align center" onClick={() => exec("justifyCenter")} />
            <TBtn icon={AlignRight} title="Align right" onClick={() => exec("justifyRight")} />
            <TBtn icon={RemoveFormatting} title="Clear formatting" onClick={clearFormatting} />

            <Divider />

            <TBtn icon={Undo2} title="Undo (Ctrl+Z)" onClick={onUndo} />
            <TBtn icon={Redo2} title="Redo (Ctrl+Shift+Z)" onClick={onRedo} />
            {onInsertInlineSocialSnippet && (
              <>
                <Divider />
                <TBtn icon={Share2} title="Insert inline social icons snippet" onClick={onInsertInlineSocialSnippet} />
              </>
            )}

            {onClearCanvas && (
              <>
                <Divider />
                <button
                  onClick={onClearCanvas}
                  className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium bg-white/15 text-bookish-purple rounded-lg border border-white/20 hover:bg-white/25 transition-colors whitespace-nowrap"
                >
                  Start Over
                </button>
              </>
            )}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-3 py-3 md:px-5 md:py-5">
          <div
            className="mx-auto w-full border border-slate-200/80 bg-white/70 px-3 py-3 shadow-[0_18px_48px_rgba(15,23,42,0.08)] ring-1 ring-inset ring-white/70 transition-all duration-300 md:px-4 md:py-4"
            style={{ maxWidth: workspaceMaxWidth }}
          >
            <div className="mx-auto overflow-hidden transition-all duration-300" style={{
              maxWidth: workspaceMaxWidth,
              ...(isMobile
                ? {
                    borderRadius: "28px",
                    border: "8px solid #1f1f1f",
                    boxShadow: "0 12px 40px rgba(0,0,0,0.15), inset 0 0 0 2px rgba(255,255,255,0.1)",
                  }
                : {
                    borderRadius: "18px",
                    border: "1px solid rgba(148, 163, 184, 0.28)",
                    boxShadow: "0 18px 40px rgba(15, 23, 42, 0.08)",
                  }),
            }}>
              {isMobile && (
                <div className="bg-[#1f1f1f] h-6 flex items-center justify-center">
                  <div className="w-16 h-1 bg-white/30 rounded-full" />
                </div>
              )}
              <div
                style={{
                  background: isMobile ? cardBg : outerBg,
                  padding: isMobile ? `0 ${editorPadding} 0 ${editorPadding}` : "20px",
                }}
              >
                <div
                  style={{
                    background: cardBg,
                    borderRadius: isMobile ? "0" : "14px",
                    boxShadow: isMobile ? "none" : "0 10px 24px rgba(15, 23, 42, 0.06)",
                    padding: `0 ${editorPadding} 0 ${editorPadding}`,
                  }}
                >
                  <div className="relative">
                    <div
                      ref={editorRef}
                      contentEditable
                      className="newsletter-editor min-h-[520px] focus:outline-none"
                      style={{ color: textColor, fontFamily, fontSize }}
                      onBlur={normalizeEditor}
                      onKeyDown={onKeyDown}
                      onInput={(e) => {
                        const placeholders = e.currentTarget.querySelectorAll("[data-placeholder]");
                        placeholders.forEach((el) => {
                          const hasContent = el.textContent.trim().length > 0;
                          el.setAttribute("data-empty", hasContent ? "false" : "true");
                        });
                      }}
                    ></div>

                    {/* Empty state guidance 
                    {isEmpty && !loading && (
                      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                        <div className="text-center space-y-4 pointer-events-auto p-8">
                          <div className="w-14 h-14 rounded-2xl bg-bookish-purple/8 flex items-center justify-center mx-auto">
                            <Layout size={28} className="text-bookish-purple/40" />
                          </div>
                          <div>
                            <h3 className="text-base font-semibold text-bookish-purple/70">Your newsletter starts here</h3>
                            <p className="text-sm text-bookish-purple/40 mt-1 max-w-xs mx-auto">
                              Add a block from the left panel to get started, or load a saved template.
                            </p>
                          </div>
                          <div className="flex items-center justify-center gap-2">
                            {onAddTextBlock && (
                              <button
                                type="button"
                                onClick={onAddTextBlock}
                                className="flex items-center gap-1.5 px-4 py-2 text-xs font-medium bg-bookish-purple text-white rounded-lg hover:bg-bookish-purpleAccent transition-colors"
                              >
                                <TypeIcon size={13} />
                                Add Text Block
                              </button>
                            )}
                            {onBrowseTemplates && (
                              <button
                                type="button"
                                onClick={onBrowseTemplates}
                                className="flex items-center gap-1.5 px-4 py-2 text-xs font-medium rounded-lg border border-bookish-purple/15 text-bookish-purple/70 hover:bg-bookish-purple/5 transition-colors"
                              >
                                <Columns size={13} />
                                Browse Templates
                              </button>
                            )}
                          </div>
                        </div>
                      </div> 
                    )}*/}
                  </div>
                </div>
              </div>
              {isMobile && (
                <div className="bg-[#1f1f1f] h-5 flex items-center justify-center">
                  <div className="w-24 h-1 bg-white/20 rounded-full" />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
